(() => {
  'use strict';
  if (window.NLAFixedModelSwipe) return;
  const version = '20261020-fixed-body-ios-touch-1';
  const own = (object, key) => object && Object.prototype.hasOwnProperty.call(object, key) ? object[key] : null;
  const sourceURL = value => {
    if (typeof value !== 'string' || !value.trim()) return '';
    try {
      const source = new URL(value, typeof location === 'object' ? location.href : 'https://nla-shop.ch/');
      return ['https:', 'http:'].includes(source.protocol) ? source.href : '';
    } catch { return ''; }
  };
  const finite = value => typeof value === 'number' && Number.isFinite(value);
  const productColors = item => Array.isArray(item?.colors) ? item.colors.map(color => typeof color === 'string' ? color : color?.name).filter(color => typeof color === 'string') : [];
  const error = (code, detail) => ({ code, detail });
  const localStudy = options => options.studyMode === 'local-registration' && options.provisionalStudy === true && options.qualifiedForPublication === false &&
    typeof location === 'object' && ['127.0.0.1', 'localhost', '[::1]'].includes(location.hostname);

  function validateUnderlay(base, study) {
    const underlay = base.neutralUnderlay;
    if (underlay === undefined) return [];
    const faults = [];
    if (!underlay || typeof underlay !== 'object') return [error('NEUTRAL_UNDERLAY_REQUIRED', 'neutralUnderlay must be a registered neutral image')];
    if (typeof underlay.id !== 'string' || !underlay.id || underlay.id === base.id || ['productId', 'variantId', 'color'].some(key => Object.prototype.hasOwnProperty.call(underlay, key))) faults.push(error('NEUTRAL_UNDERLAY_ID_INVALID', 'The neutral underlay must be independent of any product or color'));
    if (underlay.modelBaseId !== base.id || underlay.poseId !== base.poseId || underlay.view !== 'front' || base.view !== 'front') faults.push(error('NEUTRAL_UNDERLAY_POSE_MISMATCH', 'The neutral underlay must match the fixed front body exactly'));
    const source = sourceURL(underlay.src);
    if (!source || source === sourceURL(base.src)) faults.push(error('NEUTRAL_UNDERLAY_SOURCE_INVALID', 'A separate registered neutral image is required'));
    if (underlay.alphaVerified !== true || underlay.registrationVerified !== true || (underlay.qualified === false && !(study && underlay.provisionalStudy === true)) || (!study && (underlay.qualifiedForPublication !== true || underlay.provisionalStudy === true))) faults.push(error('NEUTRAL_UNDERLAY_UNQUALIFIED', 'Neutral alpha and registration require explicit qualification'));
    const placement = underlay.placement;
    if (!placement || !['x', 'y', 'width', 'height'].every(key => finite(placement[key])) || placement.x < 0 || placement.y < 0 || placement.width <= 0 || placement.height <= 0 || placement.x + placement.width > 1 || placement.y + placement.height > 1 || (placement.rotation !== undefined && placement.rotation !== 0) || (placement.flipX !== undefined && placement.flipX !== false)) faults.push(error('NEUTRAL_UNDERLAY_REGISTRATION_INVALID', 'The neutral underlay must be stationary within its source frame'));
    return faults;
  }

  function validateConfig(options = {}) {
    const faults = [];
    const base = options.modelBase;
    const study = localStudy(options);
    if (options.studyMode && !study) faults.push(error('STUDY_MODE_LOCAL_ONLY', 'An explicitly provisional local registration study is required'));
    if (!['women', 'men'].includes(options.gender)) faults.push(error('GENDER_REQUIRED', 'gender must be women or men'));
    if (!base || typeof base !== 'object') return [...faults, error('MODEL_BASE_REQUIRED', 'No fixed modelBase supplied')];
    if (base.qualified !== true && !(study && base.provisionalStudy === true)) faults.push(error('MODEL_BASE_UNQUALIFIED', 'modelBase.qualified must be true after asset review'));
    for (const key of ['id', 'poseId']) if (typeof base[key] !== 'string' || !base[key]) faults.push(error('MODEL_BASE_ID_REQUIRED', key));
    if (base.gender !== options.gender) faults.push(error('MODEL_BASE_GENDER_MISMATCH', 'modelBase.gender must match explicit entry gender'));
    if (!['front', 'back'].includes(base.view)) faults.push(error('MODEL_BASE_VIEW_REQUIRED', 'modelBase.view must identify the actual fixed view'));
    if (!sourceURL(base.src)) faults.push(error('MODEL_BASE_SOURCE_REQUIRED', 'modelBase.src must be an image URL'));
    if (!finite(base.width) || !finite(base.height) || base.width <= 0 || base.height <= 0) faults.push(error('MODEL_BASE_FRAME_REQUIRED', 'Positive source-frame width and height required'));
    faults.push(...validateUnderlay(base, study));
    if (!Array.isArray(options.items)) faults.push(error('ITEMS_REQUIRED', 'items must be the existing product records'));
    const ids = (Array.isArray(options.items) ? options.items : []).map(item => String(item?.id ?? ''));
    if (ids.some(id => !id) || new Set(ids).size !== ids.length) faults.push(error('PRODUCT_IDS_INVALID', 'Each product must have one unique existing ID'));
    return faults;
  }

  function validateSelection(options = {}, productId, color) {
    const configurationFaults = validateConfig(options);
    if (configurationFaults.length) return { ok: false, errors: configurationFaults };
    const faults = [];
    const id = String(productId);
    const item = options.items.find(candidate => String(candidate.id) === id);
    if (!item) return { ok: false, errors: [error('PRODUCT_NOT_FOUND', id)] };
    if (typeof color !== 'string' || !productColors(item).includes(color)) return { ok: false, errors: [error('EXACT_COLOR_NOT_FOUND', `${id}/${String(color)}`)] };
    const binding = own(own(options.bindings, id), color);
    const layer = binding?.garmentLayer;
    const base = options.modelBase;
    if (!layer || typeof layer !== 'object') return { ok: false, errors: [error('GARMENT_LAYER_REQUIRED', `${id}/${color}`)] };
    if (String(layer.productId) !== id || layer.color !== color) faults.push(error('GARMENT_ID_COLOR_MISMATCH', `${id}/${color}`));
    if (layer.modelBaseId !== base.id || layer.poseId !== base.poseId || layer.view !== base.view) faults.push(error('GARMENT_POSE_MISMATCH', `${id}/${color}`));
    if (layer.alphaVerified !== true || layer.registrationVerified !== true) faults.push(error('GARMENT_LAYER_UNQUALIFIED', `${id}/${color}`));
    if (!localStudy(options) && (layer.provisionalStudy === true || layer.qualifiedForPublication === false || layer.qualified === false)) faults.push(error('GARMENT_LAYER_STUDY_ONLY', `${id}/${color}`));
    const source = sourceURL(layer.src);
    if (!source) faults.push(error('GARMENT_SOURCE_REQUIRED', `${id}/${color}`));
    const placement = layer.placement;
    if (!placement || !['x', 'y', 'width', 'height'].every(key => finite(placement[key])) ||
        placement.x < -.5 || placement.x > 1 || placement.y < -.5 || placement.y > 1 ||
        placement.width <= 0 || placement.width > 2 || placement.height <= 0 || placement.height > 2 ||
        (placement.rotation !== undefined && (!finite(placement.rotation) || Math.abs(placement.rotation) > 45)) ||
        (placement.flipX !== undefined && placement.flipX !== false)) faults.push(error('GARMENT_REGISTRATION_INVALID', `${id}/${color}`));
    return faults.length ? { ok: false, errors: faults } : {
      ok: true, item, binding,
      layer: Object.freeze({
        productId: id, color, source,
        alt: String(layer.alt || `${item.name || item.title || ''} · ${color}`),
        placement: Object.freeze({ x: placement.x, y: placement.y, width: placement.width, height: placement.height, rotation: placement.rotation || 0 })
      }),
      errors: []
    };
  }

  function mount(host, supplied = {}) {
    if (!host || !host.ownerDocument || host.childNodes.length) throw new Error('A dedicated empty stage host is required.');
    const options = { ...supplied, items: Array.isArray(supplied.items) ? [...supplied.items] : [], modelBase: supplied.modelBase ? Object.freeze({ ...supplied.modelBase, ...(supplied.modelBase.neutralUnderlay === undefined ? {} : { neutralUnderlay: Object.freeze({ ...supplied.modelBase.neutralUnderlay, placement: Object.freeze({ ...supplied.modelBase.neutralUnderlay?.placement }) }) }) }) : null };
    const faults = validateConfig(options);
    if (faults.length) {
      const failure = new Error('Fixed model configuration is not qualified.');
      failure.details = faults;
      throw failure;
    }
    const doc = host.ownerDocument;
    const view = doc.defaultView || window;
    // Apple touch uses Touch Events so Safari scroll arbitration cannot strand a pointer.
    // Mouse, pen and Android keep their existing Pointer Event path.
    const appleTouch = 'ontouchstart' in view && (/iP(?:hone|ad|od)/.test(view.navigator?.userAgent || '') ||
      (view.navigator?.platform === 'MacIntel' && view.navigator?.maxTouchPoints > 1));
    const acceptsPointer = event => !(appleTouch && event.pointerType === 'touch');
    const touchPoint = (touch, event, ending = false) => ({
      pointerId: 'touch:' + touch.identifier, pointerType: 'touch', isPrimary: true, button: 0,
      clientX: touch.clientX, clientY: touch.clientY,
      cancelable: !ending && event.cancelable,
      preventDefault: () => { if (!ending && event.cancelable) event.preventDefault(); }
    });
    host.dataset.swipeInput = appleTouch ? 'apple-touch' : 'pointer';

    const abort = new AbortController();
    const listen = (node, type, callback, extra = {}) => node?.addEventListener(type, callback, { ...extra, signal: abort.signal });
    const base = options.modelBase;
    const baseSource = sourceURL(base.src);
    const neutral = base.neutralUnderlay;
    const duration = finite(options.duration) ? Math.max(250, Math.min(1200, options.duration)) : 780;
    const reduced = view.matchMedia('(prefers-reduced-motion: reduce)');
    const initialAttributes = { tabindex: host.getAttribute('tabindex'), role: host.getAttribute('role'), label: host.getAttribute('aria-label') };
    const hadClass = host.classList.contains('nla-fixed-model-swipe');
    host.classList.add('nla-fixed-model-swipe');
    if (!host.hasAttribute('tabindex')) host.tabIndex = 0;
    if (!host.hasAttribute('role')) host.setAttribute('role', 'region');
    if (!host.hasAttribute('aria-label')) host.setAttribute('aria-label', `${options.gender === 'women' ? 'Frauen' : 'Männer'}: Pieces mit Pfeiltasten oder Wischen wechseln`);
    host.dataset.fixedModelBaseId = base.id;
    host.dataset.fixedModelGender = base.gender;

    const frame = doc.createElement('div');
    frame.className = 'nla-fms-frame';
    const modelImage = doc.createElement('img');
    modelImage.className = 'nla-fms-model-base';
    modelImage.alt = String(base.alt || `${base.gender === 'women' ? 'Weibliches' : 'Männliches'} Model`);
    modelImage.width = base.width;
    modelImage.height = base.height;
    modelImage.decoding = 'async';
    modelImage.draggable = false;
    modelImage.style.opacity = '0';
    frame.append(modelImage);
    const neutralLayer = neutral ? doc.createElement('div') : null;
    const neutralImage = neutral ? doc.createElement('img') : null;
    if (neutralLayer) {
      neutralLayer.className = 'nla-fms-neutral-underlay';
      neutralLayer.dataset.neutralUnderlayId = neutral.id;
      neutralLayer.setAttribute('aria-hidden', 'true');
      Object.assign(neutralLayer.style, { position: 'absolute', left: `${neutral.placement.x * 100}%`, top: `${neutral.placement.y * 100}%`, width: `${neutral.placement.width * 100}%`, height: `${neutral.placement.height * 100}%`, zIndex: '2', pointerEvents: 'none', opacity: '0', visibility: 'hidden', transform: 'none', transition: 'none', animation: 'none' });
      neutralImage.alt = '';
      neutralImage.width = base.width;
      neutralImage.height = base.height;
      Object.assign(neutralImage.style, { display: 'block', width: '100%', height: '100%', objectFit: 'contain', transform: 'none', transition: 'none', animation: 'none' });
      neutralLayer.append(neutralImage);
      frame.append(neutralLayer);
    }
    host.append(frame);

    const cache = new Map();
    const colors = new Map();
    let selected = null;
    let activeLayer = null;
    let selectedIndex = 0;
    let epoch = 0;
    let destroyed = false;
    let phase = 'idle';
    let fault = null;
    let gesture = null;
    let pointerFrame = 0;
    let resizeFrame = 0;
    let animationTimer = 0;
    let suppressedUntil = 0;
    let running = [];
    let frameWidth = 0;
    let baseLoaded = false;
    let neutralLoaded = !neutral;

    function syncFoundation() {
      if (!neutralLayer) return;
      const foundationReady = baseLoaded && neutralLoaded && !destroyed;
      const record = activeRecord();
      const moving = !reduced.matches && (gesture?.active === true || motionFrame > 0);
      const covered = activeLayer?.isConnected && record && Math.abs(record.x) < .5 && !moving;
      const visible = foundationReady && (!covered || moving);
      neutralLayer.style.opacity = visible ? '1' : '0';
      neutralLayer.style.visibility = visible ? 'visible' : 'hidden';
      modelImage.style.opacity = foundationReady ? '1' : '0';
    }

    function state() {
      const displayed = activeRecord();
      const shown = phase === 'loading' && displayed && !isCurrent(displayed) ? displayed.entry : selected;
      const displayedIndex = shown?.layer ? options.items.findIndex(item => String(item.id) === shown.layer.productId) : selectedIndex;
      return Object.freeze({
        version, gender: base.gender, modelBaseId: base.id, poseId: base.poseId,
        provisionalStudy: localStudy(options), qualifiedForPublication: false,
        productId: shown?.layer?.productId || shown?.productId || null,
        color: shown?.layer?.color ?? shown?.color ?? null,
        requestedProductId: selected?.layer?.productId || selected?.productId || null,
        requestedColor: selected?.layer?.color ?? selected?.color ?? null,
        index: options.items.length ? displayedIndex : null, count: options.items.length,
        phase, ready: phase === 'ready', error: fault,
        neutralUnderlayId: neutral?.id || null, neutralUnderlayVisible: neutralLayer?.style.visibility === 'visible',
        item: shown?.item || null, binding: shown?.binding || null
      });
    }
    function emit() {
      syncFoundation();
      const current = state();
      host.dataset.fixedModelPhase = phase;
      host.dataset.productId = current.productId || '';
      host.dataset.color = current.color || '';
      host.setAttribute('aria-busy', String(phase === 'loading'));
      if (typeof options.onSelect === 'function') options.onSelect(current);
      host.dispatchEvent(new CustomEvent('fixedmodel:change', { detail: current }));
    }
    function fit() {
      resizeFrame = 0;
      if (destroyed) return;
      const ratio = base.width / base.height;
      const width = host.clientWidth;
      const height = host.clientHeight;
      frameWidth = Math.min(width, height * ratio);
      frame.style.width = `${frameWidth}px`;
      frame.style.height = `${frameWidth / ratio}px`;
    }
    const queueFit = () => { if (!resizeFrame) resizeFrame = view.requestAnimationFrame(fit); };

    function loadImage(source, existing) {
      const image = existing || doc.createElement('img');
      image.decoding = 'async';
      image.draggable = false;
      let resolve;
      let finished = false;
      let decoding = false;
      const entry = { image, state: 'loading', promise: new Promise(done => { resolve = done; }), cancel: null };
      function finish(ok) {
        if (finished) return;
        finished = true;
        image.onload = null;
        image.onerror = null;
        entry.state = ok ? 'ready' : 'error';
        resolve(ok);
      }
      image.onload = async () => {
        if (decoding || finished) return;
        decoding = true;
        try { if (image.decode) await image.decode(); } catch {}
        finish(image.naturalWidth > 0);
      };
      image.onerror = () => finish(false);
      entry.cancel = () => { finish(false); image.removeAttribute('src'); };
      image.src = source;
      if (image.complete) {
        if (image.naturalWidth > 0) void image.onload();
        else image.onerror();
      }
      return entry;
    }
    const baseAsset = loadImage(baseSource, modelImage);
    const baseReady = baseAsset.promise.then(ok => {
      if (destroyed) return false;
      baseLoaded = ok;
      if (ok && !neutral) modelImage.style.opacity = '1';
      syncFoundation();
      return ok;
    });
    const neutralAsset = neutral ? loadImage(sourceURL(neutral.src), neutralImage) : null;
    const neutralReady = neutralAsset ? neutralAsset.promise.then(ok => {
      if (destroyed) return false;
      neutralLoaded = ok;
      syncFoundation();
      return ok;
    }) : Promise.resolve(true);
    function asset(source) {
      if (!cache.has(source)) cache.set(source, loadImage(source));
      return cache.get(source);
    }
    const layers = new Set();
    let motionFrame = 0;
    let motionStarted = 0;
    let motionLast = 0;
    const now = () => performance.now();
    const clamp = (value, low, high) => Math.max(low, Math.min(high, value));
    const stride = () => Math.max(120, frameWidth * 1.04);
    const activeRecord = () => [...layers].find(record => record.node === activeLayer) || null;
    const isCurrent = record => record && record.entry.layer.productId === selected?.layer?.productId && record.entry.layer.color === selected?.layer?.color;

    function draw(record) {
      const turn = clamp(record.x / stride(), -1.5, 1.5) * 2;
      const rise = -Math.min(4, Math.abs(record.x / stride()) * 4);
      record.node.style.transform = `translate3d(${record.x}px,${rise}px,0) rotate(${record.entry.layer.placement.rotation + turn}deg)`;
      record.node.style.opacity = '1';
    }
    function removeRecord(record) {
      record.node.remove();
      layers.delete(record);
    }
    function stopMotion() {
      if (motionFrame) view.cancelAnimationFrame(motionFrame);
      motionFrame = 0;
      if (destroyed) {
        for (const record of [...layers]) removeRecord(record);
      }
      syncFoundation();
    }
    function atRest(layer) {
      const record = [...layers].find(candidate => candidate.node === layer);
      if (!record) return;
      record.x = record.target = record.v = 0;
      draw(record);
    }
    function desiredColor(item) {
      const id = String(item.id);
      const remembered = colors.get(id);
      if (remembered !== undefined) return remembered;
      const suppliedColor = own(options.initialColors, id);
      if (typeof suppliedColor === 'string') return suppliedColor;
      if (typeof item.initialColor === 'string') return item.initialColor;
      return productColors(item)[0] ?? '';
    }
    function warmAdjacent() {
      if (!selected?.layer || destroyed) return;
      const required = new Set([selected.layer.source]);
      for (const record of layers) required.add(record.entry.layer.source);
      if (options.items.length > 1) {
        for (const step of [-1, 1]) {
          const item = options.items[(selectedIndex + step + options.items.length) % options.items.length];
          const entry = validateSelection(options, item.id, desiredColor(item));
          if (entry.ok) { required.add(entry.layer.source); asset(entry.layer.source); }
        }
      }
      for (const [source, cached] of cache) {
        if (cache.size <= 7) break;
        if (!required.has(source)) { cached.cancel(); cache.delete(source); }
      }
    }
    function makeLayer(entry, image) {
      const layer = doc.createElement('div');
      const placement = entry.layer.placement;
      layer.className = 'nla-fms-garment-layer';
      if (neutral) layer.style.zIndex = '4';
      layer.dataset.productId = entry.layer.productId;
      layer.dataset.color = entry.layer.color;
      layer.dataset.rotation = String(placement.rotation);
      layer.style.left = `${placement.x * 100}%`;
      layer.style.top = `${placement.y * 100}%`;
      layer.style.width = `${placement.width * 100}%`;
      layer.style.height = `${placement.height * 100}%`;
      layer.style.opacity = '1';
      image.alt = entry.layer.alt;
      image.className = 'nla-fms-garment-image';
      layer.append(image);
      return layer;
    }
    function recordFor(entry, x) {
      let record = [...layers].find(value => value.entry.layer.source === entry.layer.source);
      if (record) return record;
      const loaded = asset(entry.layer.source);
      const node = makeLayer(entry, loaded.image);
      record = { node, entry, x, target: x, v: 0, origin: x };
      layers.add(record);
      draw(record);
      frame.append(node);
      const surplus = [...layers].filter(value => value !== record && value.node !== activeLayer).sort((a, b) => Math.abs(b.x) - Math.abs(a.x));
      while (layers.size > 3 && surplus.length) removeRecord(surplus.shift());
      return record;
    }
    function finishRest() {
      const current = activeRecord();
      for (const record of [...layers]) {
        if (record !== current) removeRecord(record);
      }
      if (current) atRest(current.node);
      if (isCurrent(current)) {
        phase = 'ready';
        emit();
        warmAdjacent();
      } else syncFoundation();
    }
    function tick(timestamp) {
      motionFrame = 0;
      if (destroyed || gesture?.active) return;
      const time = finite(timestamp) ? timestamp : now();
      const dt = clamp((time - motionLast) / 1000, 1 / 240, .05);
      motionLast = time;
      const omega = 10000 / duration;
      const decay = Math.exp(-omega * dt);
      let moving = false;
      for (const record of layers) {
        const delta = record.x - record.target;
        const tangent = record.v + omega * delta;
        record.x = record.target + (delta + tangent * dt) * decay;
        record.v = (record.v - omega * tangent * dt) * decay;
        moving ||= Math.abs(record.x - record.target) > .12 || Math.abs(record.v) > 3;
        draw(record);
      }
      if (moving && time - motionStarted < Math.max(450, duration * 1.7)) {
        motionFrame = view.requestAnimationFrame(tick);
        syncFoundation();
      } else finishRest();
    }
    function moveToRest(target, direction = 1, releaseVelocity) {
      stopMotion();
      if (!target) return;
      for (const record of layers) {
        const sign = Math.sign(record.x - target.x) || -direction;
        record.target = record === target ? 0 : sign * stride();
        if (finite(releaseVelocity)) record.v = releaseVelocity;
        if (record === target) {
          delete record.node.dataset.fmsOutgoing;
          record.node.removeAttribute('aria-hidden');
          if (neutral) record.node.style.zIndex = '4';
        } else {
          record.node.dataset.fmsOutgoing = '';
          record.node.setAttribute('aria-hidden', 'true');
          if (neutral) record.node.style.zIndex = '3';
        }
      }
      if (reduced.matches || [...layers].every(record => Math.abs(record.x - record.target) < .12 && Math.abs(record.v) < 3)) { finishRest(); return; }
      motionStarted = motionLast = now();
      motionFrame = view.requestAnimationFrame(tick);
      syncFoundation();
    }
    function cancelCapture() {
      if (!gesture) return null;
      const ended = gesture;
      if (pointerFrame) { view.cancelAnimationFrame(pointerFrame); pointerFrame = 0; }
      if (ended.active) paintPointer();
      gesture = null;
      host.classList.remove('is-dragging');
      if (!ended.touch) { try { if (host.hasPointerCapture?.(ended.id)) host.releasePointerCapture(ended.id); } catch { /* Safari can release capture before pointerup. */ } }
      return ended;
    }

    async function select(request) {
      if (destroyed) return false;
      cancelCapture();
      const productId = String(request?.productId ?? '');
      const requestedColor = request?.color;
      const requestedIndex = options.items.findIndex(item => String(item.id) === productId);
      const entry = validateSelection(options, productId, requestedColor);
      const token = ++epoch;
      const previous = activeRecord();
      selectedIndex = requestedIndex < 0 ? 0 : requestedIndex;
      selected = entry.ok ? entry : { productId, color: requestedColor, item: options.items[requestedIndex] || null };
      phase = entry.ok ? 'loading' : 'unready';
      fault = entry.ok ? null : entry.errors;
      if (!entry.ok) {
        stopMotion();
        for (const record of [...layers]) removeRecord(record);
        activeLayer = null;
        emit();
        return false;
      }
      colors.set(productId, requestedColor);
      const loaded = asset(entry.layer.source);
      const cached = loaded.state === 'ready' && baseLoaded && neutralLoaded;
      if (!cached && previous) moveToRest(previous);
      warmAdjacent();
      emit();
      const ready = await Promise.all([baseReady, loaded.promise, neutralReady]);
      if (destroyed || token !== epoch) return false;
      if (!ready[0] || !ready[1] || !ready[2]) {
        stopMotion();
        for (const record of [...layers]) removeRecord(record);
        activeLayer = null;
        phase = 'error';
        fault = [error(!ready[0] ? 'MODEL_BASE_LOAD_FAILED' : !ready[2] ? 'NEUTRAL_UNDERLAY_LOAD_FAILED' : 'GARMENT_LOAD_FAILED', `${productId}/${requestedColor}`)];
        emit();
        return false;
      }
      const direction = request.direction === -1 ? -1 : 1;
      const incoming = recordFor(entry, previous ? previous.x + stride() * direction : stride() * direction);
      activeLayer = incoming.node;
      if (request.animate === false || reduced.matches || !previous || (incoming === previous && Math.abs(incoming.x) < .12 && Math.abs(incoming.v) < 3)) {
        stopMotion();
        finishRest();
        return true;
      }
      moveToRest(incoming, direction, request.releaseVelocity);
      return true;
    }
    function step(direction, releaseVelocity) {
      if (destroyed || options.items.length < 2) return false;
      const index = (selectedIndex + direction + options.items.length) % options.items.length;
      const item = options.items[index];
      return select({ productId: item.id, color: desiredColor(item), direction, releaseVelocity });
    }
    function open() {
      if (destroyed || phase !== 'ready' || !selected?.layer) return false;
      if (typeof options.onOpen === 'function') options.onOpen(state());
      return true;
    }
    function warmGesture(held) {
      if (options.items.length < 2 || !selected?.layer) return;
      const reference = activeRecord();
      if (!reference) return;
      const seen = new Set();
      for (const direction of options.items.length === 2 ? [held.dx < 0 ? 1 : -1] : [-1, 1]) {
        const item = options.items[(selectedIndex + direction + options.items.length) % options.items.length];
        const entry = validateSelection(options, item.id, desiredColor(item));
        if (!entry.ok || seen.has(entry.layer.source)) continue;
        seen.add(entry.layer.source);
        const attach = () => {
          if (destroyed || gesture !== held || !held.active || held.epoch !== epoch) return;
          let record = [...layers].find(value => value.entry.layer.source === entry.layer.source);
          if (!record) record = recordFor(entry, reference.origin + direction * stride());
          record.origin ??= record.x;
          record.node.dataset.fmsOutgoing = '';
          record.node.setAttribute('aria-hidden', 'true');
          if (neutral) record.node.style.zIndex = '3';
          paintPointer();
        };
        const loaded = asset(entry.layer.source);
        if (loaded.state === 'ready') attach();
        else void loaded.promise.then(ok => { if (ok) attach(); });
      }
    }
    function paintPointer() {
      if (!gesture?.active || !activeLayer || reduced.matches) return;
      const x = clamp(gesture.dx, -stride(), stride());
      for (const record of layers) {
        record.x = record.origin + x;
        record.v = gesture.velocity;
        draw(record);
      }
    }
    function endGesture(cancelled = false) {
      const ended = cancelCapture();
      if (!ended?.active) { syncFoundation(); return; }
      suppressedUntil = now() + 350;
      const threshold = Math.max(30, Math.min(70, frameWidth * .13));
      const recent = now() - ended.lastTime < 90;
      const velocity = recent ? ended.velocity : 0;
      const fast = Math.abs(ended.dx) > 18 && Math.abs(velocity) > 450 && Math.sign(velocity) === Math.sign(ended.dx);
      if (!cancelled && (Math.abs(ended.dx) >= threshold || fast)) void step(ended.dx < 0 ? 1 : -1, velocity);
      else moveToRest(activeRecord(), 1, velocity);
    }
    // NLA_IOS_INPUT_START
    function beginPointer(event) {
      if (!acceptsPointer(event) && typeof event.pointerId !== 'string') return;
      if (!activeLayer || !activeLayer.isConnected || options.items.length < 2 || event.isPrimary === false || event.button !== 0 || gesture || destroyed) return;
      const current = activeRecord();
      if (!current) return;
      if (!isCurrent(current)) {
        epoch += 1;
        selected = current.entry;
        selectedIndex = options.items.findIndex(item => String(item.id) === current.entry.layer.productId);
        phase = 'ready';
        fault = null;
        emit();
      }
      gesture = { id: event.pointerId, touch: appleTouch && typeof event.pointerId === 'string', x: event.clientX, y: event.clientY, dx: 0, active: false, started: now(), lastTime: now(), lastDx: 0, velocity: 0, epoch };
    }
    function movePointer(event) {
      if (!gesture || event.pointerId !== gesture.id) return;
      const dx = event.clientX - gesture.x;
      const dy = event.clientY - gesture.y;
      if (!gesture.active) {
        if (Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx) * 1.15) { endGesture(true); return; }
        if (Math.abs(dx) < (gesture.touch ? 6 : 9) || Math.abs(dx) < Math.abs(dy) * (gesture.touch ? 1.05 : 1.15)) return;
        stopMotion();
        for (const record of layers) record.origin = record.x;
        gesture.active = true;
        gesture.dx = dx;
        host.classList.add('is-dragging');
        if (!gesture.touch) { try { host.setPointerCapture?.(gesture.id); } catch { /* Capture may already have ended. */ } }
        warmGesture(gesture);
        syncFoundation();
      }
      if (event.cancelable) event.preventDefault();
      const time = now();
      const elapsed = Math.max(8, time - gesture.lastTime);
      const sampled = clamp((dx - gesture.lastDx) / elapsed * 1000, -2400, 2400);
      gesture.velocity = gesture.velocity * .25 + sampled * .75;
      gesture.lastTime = time;
      gesture.lastDx = dx;
      gesture.dx = dx;
      if (!pointerFrame && !reduced.matches) pointerFrame = view.requestAnimationFrame(() => { pointerFrame = 0; paintPointer(); });
    }
    listen(host, 'pointerdown', event => { if (acceptsPointer(event)) beginPointer(event); });
    listen(host, 'pointermove', event => { if (acceptsPointer(event)) movePointer(event); }, { passive: false });
    listen(view, 'pointerup', event => { if (acceptsPointer(event) && event.pointerId === gesture?.id) endGesture(); }, { capture: true });
    listen(view, 'pointercancel', event => { if (acceptsPointer(event) && event.pointerId === gesture?.id) endGesture(true); }, { capture: true });
    listen(host, 'lostpointercapture', event => { if (!gesture?.touch && event.target === host && event.pointerId === gesture?.id) endGesture(true); });
    listen(view, 'blur', () => endGesture(true));
    listen(view, 'pagehide', () => endGesture(true));
    listen(doc, 'visibilitychange', () => { if (doc.hidden) endGesture(true); });
    if (appleTouch) {
      listen(host, 'touchstart', event => {
        if (event.touches.length !== 1) { endGesture(true); return; }
        if (gesture) endGesture(true);
        beginPointer(touchPoint(event.touches[0], event));
      }, { passive: true });
      listen(host, 'touchmove', event => {
        if (!gesture?.touch) return;
        if (event.touches.length !== 1) { endGesture(true); return; }
        const point = event.touches[0];
        if ('touch:' + point.identifier === gesture.id) movePointer(touchPoint(point, event));
      }, { passive: false });
      listen(view, 'touchend', event => {
        if (!gesture?.touch) return;
        const point = Array.from(event.changedTouches).find(touch => 'touch:' + touch.identifier === gesture.id);
        if (!point) return;
        // Include final coordinates even if Safari coalesced the last touchmove.
        movePointer(touchPoint(point, event, true));
        endGesture();
      }, { passive: true, capture: true });
      listen(view, 'touchcancel', () => { if (gesture?.touch) endGesture(true); }, { passive: true, capture: true });
    }
    // NLA_IOS_INPUT_END
    listen(host, 'click', event => {
      if (now() < suppressedUntil) { event.preventDefault(); event.stopImmediatePropagation(); return; }
      if (event.target === host || frame.contains(event.target)) open();
    }, { capture: true });
    listen(host, 'dragstart', event => event.preventDefault());
    listen(host, 'keydown', event => {
      if (event.target !== host || !['ArrowLeft', 'ArrowRight', 'Home', 'End', 'Enter', ' '].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'ArrowLeft') void step(-1);
      else if (event.key === 'ArrowRight') void step(1);
      else if (event.key === 'Enter' || event.key === ' ') open();
      else if (options.items.length) {
        const item = options.items[event.key === 'Home' ? 0 : options.items.length - 1];
        void select({ productId: item.id, color: desiredColor(item), direction: event.key === 'Home' ? -1 : 1 });
      }
    });
    const finishMotion = () => {
      if (!reduced.matches) { syncFoundation(); return; }
      cancelCapture();
      stopMotion();
      finishRest();
    };
    listen(reduced, 'change', finishMotion);

    const resize = typeof view.ResizeObserver === 'function' ? new view.ResizeObserver(queueFit) : null;
    resize?.observe(host);
    listen(view, 'resize', queueFit, { passive: true });
    listen(options.controls?.previous, 'click', () => { void step(-1); });
    listen(options.controls?.next, 'click', () => { void step(1); });
    listen(options.controls?.open, 'click', open);
    fit();

    const controller = Object.freeze({
      version, select, next: () => step(1), previous: () => step(-1), open,
      getState: state,
      destroy() {
        if (destroyed) return;
        endGesture(true);
        destroyed = true;
        epoch += 1;
        stopMotion();
        abort.abort();
        resize?.disconnect();
        if (pointerFrame) view.cancelAnimationFrame(pointerFrame);
        if (resizeFrame) view.cancelAnimationFrame(resizeFrame);
        baseAsset.cancel();
        neutralAsset?.cancel();
        syncFoundation();
        for (const cached of cache.values()) cached.cancel();
        cache.clear();
        frame.remove();
        host.classList.remove('is-dragging');
        if (!hadClass) host.classList.remove('nla-fixed-model-swipe');
        for (const [attribute, value] of [['tabindex', initialAttributes.tabindex], ['role', initialAttributes.role], ['aria-label', initialAttributes.label]]) {
          if (value === null) host.removeAttribute(attribute); else host.setAttribute(attribute, value);
        }
        for (const attribute of ['data-fixed-model-base-id', 'data-fixed-model-gender', 'data-fixed-model-phase', 'data-product-id', 'data-color', 'aria-busy']) host.removeAttribute(attribute);
      }
    });
    if (options.items.length) {
      const requested = String(options.initialProductId ?? options.items[0].id);
      const item = options.items.find(candidate => String(candidate.id) === requested);
      void select({ productId: requested, color: typeof options.initialColor === 'string' ? options.initialColor : item ? desiredColor(item) : '', animate: false });
    } else emit();
    return controller;
  }

  window.NLAFixedModelSwipe = Object.freeze({ version, validateConfig, validateSelection, mount });
})();
